import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState
} from 'react';

const LayeredModalContext = createContext(null);
let nextLayerId = 1;

export function computeLayerViewport(viewportWidth, viewportHeight) {
  if (!Number.isFinite(viewportWidth) || !Number.isFinite(viewportHeight) || viewportWidth <= 0 || viewportHeight <= 0) {
    throw new Error('positive viewport dimensions required');
  }
  if (viewportWidth <= 640) {
    return { width: Math.max(0, viewportWidth - 16), height: Math.max(0, viewportHeight - 16), mode: 'phone-safe' };
  }
  return { width: viewportWidth * 0.95, height: viewportHeight * 0.95, mode: 'workspace-95' };
}

function focusLater(element) {
  if (!element || typeof element.focus !== 'function') return;
  setTimeout(function () { element.focus(); }, 0);
}

export function LayeredModalProvider({ children }) {
  const [layers, setLayers] = useState([]);
  const openerById = useRef({});

  const openLayer = useCallback(function (spec) {
    if (!spec || !spec.title || !spec.content) throw new Error('layer title and content required');
    const id = spec.id || ('cp-layer-' + nextLayerId++);
    openerById.current[id] = (typeof document !== 'undefined') ? document.activeElement : null;
    setLayers(function (current) {
      if (current.some(function (layer) { return layer.id === id; })) return current;
      return current.concat([{ ...spec, id: id }]);
    });
    return id;
  }, []);

  const closeTop = useCallback(function () {
    setLayers(function (current) {
      if (!current.length) return current;
      const removed = current[current.length - 1];
      const opener = openerById.current[removed.id];
      delete openerById.current[removed.id];
      focusLater(opener);
      return current.slice(0, -1);
    });
  }, []);

  const closeAll = useCallback(function () {
    setLayers(function (current) {
      if (!current.length) return current;
      const opener = openerById.current[current[0].id];
      openerById.current = {};
      focusLater(opener);
      return [];
    });
  }, []);

  return (
    <LayeredModalContext.Provider value={{ layers, openLayer, closeTop, closeAll }}>
      {children}
    </LayeredModalContext.Provider>
  );
}

export function useLayeredModal() {
  const value = useContext(LayeredModalContext);
  if (!value) throw new Error('useLayeredModal must be used inside LayeredModalProvider');
  return value;
}

function focusableElements(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter(function (element) { return element.getAttribute('aria-hidden') !== 'true'; });
}

export function LayeredModalHost() {
  const { layers, closeTop, closeAll } = useLayeredModal();
  const dialogRefs = useRef({});
  const priorBodyOverflow = useRef('');
  const priorLayerCount = useRef(0);
  const top = layers[layers.length - 1];
  const topId = top ? top.id : null;
  const stackOpen = layers.length > 0;

  useEffect(function () {
    if (!stackOpen || typeof document === 'undefined') return undefined;
    priorBodyOverflow.current = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return function () { document.body.style.overflow = priorBodyOverflow.current; };
  }, [stackOpen]);

  useEffect(function () {
    if (!topId || typeof document === 'undefined') {
      priorLayerCount.current = layers.length;
      return undefined;
    }
    const dialog = dialogRefs.current[topId];
    const focusables = focusableElements(dialog);
    if (layers.length > priorLayerCount.current) {
      focusLater((dialog && dialog.querySelector('[data-cp-autofocus="true"]')) || focusables[0] || dialog);
    }
    priorLayerCount.current = layers.length;

    function onKeyDown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeTop();
        return;
      }
      if (event.key !== 'Tab') return;
      const activeDialog = dialogRefs.current[topId];
      const available = focusableElements(activeDialog);
      if (!available.length) {
        event.preventDefault();
        if (activeDialog) activeDialog.focus();
        return;
      }
      const first = available[0];
      const last = available[available.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown, true);
    return function () { document.removeEventListener('keydown', onKeyDown, true); };
  }, [topId, layers.length, closeTop]);

  if (!layers.length) return null;
  return (
    <div className="cp-layer-stack" data-layer-count={layers.length}>
      {layers.map(function (layer, index) {
        const isTop = index === layers.length - 1;
        return (
          <div
            className={'cp-layer-overlay' + (isTop ? ' is-top' : ' is-background')}
            key={layer.id}
            data-layer-id={layer.id}
            style={{ zIndex: 4100 + index }}
            onMouseDown={function (event) { event.preventDefault(); event.stopPropagation(); }}
          >
            <section
              className="cp-layer-dialog"
              role="dialog"
              aria-modal={isTop ? 'true' : undefined}
              aria-hidden={isTop ? undefined : 'true'}
              aria-labelledby={layer.id + '-title'}
              tabIndex="-1"
              ref={function (node) { dialogRefs.current[layer.id] = node; }}
              onMouseDown={function (event) { event.stopPropagation(); }}
            >
              <header className="cp-layer-head">
                <div className="cp-layer-heading">
                  {layer.eyebrow ? <div className="cp-layer-eyebrow">{layer.eyebrow}</div> : null}
                  <h2 id={layer.id + '-title'}>{layer.title}</h2>
                  {layer.subtitle ? <p>{layer.subtitle}</p> : null}
                  {layers.length > 1 ? (
                    <nav className="cp-layer-trail" aria-label="Workspace context">
                      {layers.slice(0, index + 1).map(function (entry, trailIndex) {
                        return <span key={entry.id}>{trailIndex ? ' / ' : ''}{entry.title}</span>;
                      })}
                    </nav>
                  ) : null}
                </div>
                <div className="cp-layer-head-actions">
                  {index > 0 ? <button type="button" className="cp-action-pill" onClick={closeTop}>Back</button> : null}
                  <button type="button" className="cp-layer-close" aria-label={'Close ' + layer.title + (index > 0 ? ' and return' : '')} onClick={index > 0 ? closeTop : closeAll}>{'\u00d7'}</button>
                </div>
              </header>
              <div className="cp-layer-body" data-layer-scroll={layer.id}>
                {layer.content}
              </div>
            </section>
          </div>
        );
      })}
    </div>
  );
}

export function WorkspaceChip({ children, onClick, label }) {
  return <button type="button" className="cp-workspace-chip" aria-label={label} onClick={onClick}>{children}</button>;
}

export function ActionPill({ children, onClick, disabled, primary, label }) {
  return <button type="button" className={'cp-action-pill' + (primary ? ' is-primary' : '')} aria-label={label} disabled={disabled} onClick={onClick}>{children}</button>;
}

export function StatusBadge({ children, tone }) {
  return <span className={'cp-status-badge cp-status-' + (tone || 'ready')}>{children}</span>;
}

export function WorkspaceCard({ title, type, source, status, summary, meta, onOpen, openLabel }) {
  const inner = (
    <>
      <div className="cp-workspace-card-top">
        <span className="cp-workspace-card-type">{type}</span>
        {status ? <StatusBadge tone={String(status).toLowerCase()}>{status}</StatusBadge> : null}
      </div>
      <strong className="cp-workspace-card-title">{title}</strong>
      {summary ? <span className="cp-workspace-card-summary">{summary}</span> : null}
      {meta ? <span className="cp-workspace-card-meta">{meta}</span> : null}
      {source ? <span className="cp-workspace-card-source">Source: {source}</span> : null}
    </>
  );
  if (!onOpen) return <article className="cp-workspace-card">{inner}</article>;
  return <button type="button" className="cp-workspace-card is-actionable" aria-label={openLabel || ('Open ' + title)} onClick={onOpen}>{inner}</button>;
}
